import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import afdelingen from '../data/afdelingen.json';

// ── Helpers ────────────────────────────────────────────────────────────────────

function datFmt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function urgentieTekst(dagen, prioriteit) {
  const d = parseInt(dagen);
  if (prioriteit === 'spoed') return { txt: '🔴 SPOED',               kleur: '#C0392B', bg: '#FDEDEC' };
  if (d < 0)                  return { txt: `⚠ ${Math.abs(d)}d TE LAAT`, kleur: '#C0392B', bg: '#FDEDEC' };
  if (d === 0)                return { txt: '⚠ VANDAAG',              kleur: '#C0392B', bg: '#FDEDEC' };
  if (prioriteit === 'hoog')  return { txt: `${d}d — HOOG`,           kleur: '#854F0B', bg: '#FDF3E3' };
  if (d <= 2)                 return { txt: `${d}d`,                  kleur: '#854F0B', bg: '#FDF3E3' };
  return                             { txt: `${d}d`,                  kleur: '#27500A', bg: '#fff'    };
}

function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// ── HTML-generator voor het printvenster ──────────────────────────────────────
// Genereert een volledig standalone HTML-document — geen React, geen sidebar,
// alle stijlen inline zodat browserinstellingen er niets aan kunnen doen.

function maakPrintHTML(afdsMetStappen, datumLang, logoUrl) {
  const blokken = afdsMetStappen.map(({ afd, lijst }, blokIdx) => {
    const isLaatste = blokIdx === afdsMetStappen.length - 1;

    const rijen = lijst.map((stap, i) => {
      const urg    = urgentieTekst(stap.dagen_resterend, stap.prioriteit);
      const rijBg  = urg.bg !== '#fff' ? urg.bg : i % 2 === 0 ? '#ffffff' : '#f8f9fc';
      const specs  = [
        stap.diameter_mm     ? `⌀ ${parseFloat(stap.diameter_mm)} mm`       : '',
        stap.tanden_per_stuk ? `${stap.tanden_per_stuk} tanden/stuk`         : '',
        stap.is_extern       ? `▶ Extern: ${esc(stap.externe_partij || '')}` : '',
      ].filter(Boolean).join('  ·  ');

      return `
        <tr style="background:${rijBg}; border-bottom:1px solid #EEEEEE;">
          <td style="padding:5px 7px; vertical-align:top; width:72px; white-space:nowrap;">
            <div style="font-weight:800; font-size:9px; color:${urg.kleur}; margin-bottom:2px;">${urg.txt}</div>
            <div style="font-size:8px; color:#555;">📅 ${datFmt(stap.leverdatum)}</div>
          </td>
          <td style="padding:5px 7px; vertical-align:top;">
            <div style="font-size:10px; margin-bottom:1px;">
              <span style="font-weight:700; color:#1F3864;">${esc(stap.ordernummer)}</span>
              <span style="color:#BBBBBB; margin:0 4px;">·</span>
              <span style="font-weight:600; color:#222222;">${esc(stap.klant)}</span>
            </div>
            <div style="font-size:9px; color:#555555; margin-top:1px;">
              <em>${esc(stap.label || stap.operatie_naam)}</em>${stap.artikel_omschrijving ? ` — ${esc(stap.artikel_omschrijving)}` : ''}
            </div>
            ${specs ? `<div style="font-size:8px; color:#777777; margin-top:1px;">${specs}</div>` : ''}
            ${stap.klant_referentie ? `<div style="font-size:8px; color:#999; margin-top:1px;">ref: ${esc(stap.klant_referentie)}</div>` : ''}
          </td>
          <td style="padding:5px 7px; text-align:center; font-weight:700; font-size:11px; color:#222222; vertical-align:top; width:32px;">${stap.aantal}</td>
          <td style="padding:5px 7px; font-size:9px; color:${stap.instructies ? '#333333' : '#CCCCCC'}; font-style:${stap.instructies ? 'normal' : 'italic'}; vertical-align:top;">${stap.instructies ? esc(stap.instructies) : '—'}</td>
          <td style="padding:5px 7px; text-align:center; vertical-align:top; width:28px;">
            <input type="checkbox" style="width:13px; height:13px; cursor:pointer; accent-color:#1F3864;" />
          </td>
        </tr>`;
    }).join('');

    const matItems = lijst.filter(s => s.materiaal_beschrijving || s.materiaal_referentie);
    const matBlok  = matItems.length ? `
      <div style="padding:6px 16px; background:#FFF8F0; border-top:1px solid #F0D0A0;">
        <span style="font-size:10px; font-weight:700; color:#8A5C00;">📦 Materiaal:&nbsp;</span>
        ${matItems.map(s =>
          `<span style="font-size:10px; color:#5C3D00; margin-right:12px;">
            <strong>${esc(s.ordernummer)}</strong>: ${esc(s.materiaal_beschrijving || '')}${s.materiaal_referentie ? ` (${esc(s.materiaal_referentie)})` : ''}
            ${s.materiaal_status !== 'in_stock' ? `<span style="color:#C0392B; font-weight:600;"> [${s.materiaal_status === 'te_bestellen' ? 'te bestellen' : esc(s.materiaal_status)}]</span>` : ''}
          </span>`
        ).join('')}
      </div>` : '';

    return `
      <div style="margin-bottom:${isLaatste ? '0' : '20px'}; border:1px solid #E0E0E0; overflow:hidden; ${isLaatste ? '' : 'page-break-after:always;'}">

        <!-- Afdeling header -->
        <div style="background:#1F3864; padding:9px 16px; display:flex; align-items:center; gap:12px;">
          <!-- Logo links in wit kader -->
          <div style="flex:0 0 auto; background:#ffffff; border-radius:4px; padding:3px 8px; display:flex; align-items:center;">
            <img src="${logoUrl}" alt="Spoo Linssen" style="height:26px; display:block;" />
          </div>
          <!-- Afdelingsnaam gecentreerd -->
          <div style="flex:1; text-align:center;">
            <div style="font-size:9px; color:rgba(255,255,255,0.6); text-transform:uppercase; letter-spacing:1.5px; margin-bottom:2px;">Productieplanning</div>
            <div style="font-size:17px; font-weight:800; color:#ffffff; letter-spacing:0.3px;">${esc(afd.naam)}</div>
          </div>
          <!-- Datum en aantal rechts -->
          <div style="flex:0 0 auto; text-align:right;">
            <div style="font-size:9px; color:rgba(255,255,255,0.55); margin-bottom:1px;">Afgedrukt op</div>
            <div style="font-size:11px; color:#ffffff; font-weight:500;">${datumLang}</div>
            <div style="font-size:10px; color:rgba(255,255,255,0.6); margin-top:3px;">${lijst.length} taak${lijst.length !== 1 ? 'en' : ''}</div>
          </div>
        </div>

        <!-- Tabel -->
        <table style="width:100%; border-collapse:collapse; font-size:10px; font-family:Arial,sans-serif;">
          <thead>
            <tr style="background:#F0F4FA; border-bottom:1px solid #D0D8E8;">
              <th style="padding:4px 7px; text-align:left; font-weight:700; font-size:9px; color:#1F3864; width:72px;">Urgentie / Datum</th>
              <th style="padding:4px 7px; text-align:left; font-weight:700; font-size:9px; color:#1F3864;">Order / Klant / Artikel</th>
              <th style="padding:4px 7px; text-align:center; font-weight:700; font-size:9px; color:#1F3864; width:32px;">Aant.</th>
              <th style="padding:4px 7px; text-align:left; font-weight:700; font-size:9px; color:#1F3864;">Instructies</th>
              <th style="padding:4px 7px; text-align:center; font-weight:700; font-size:9px; color:#1F3864; width:28px;">✓</th>
            </tr>
          </thead>
          <tbody>${rijen}</tbody>
        </table>

        ${matBlok}
      </div>`;
  }).join('');

  return `<!DOCTYPE html>
<html lang="nl">
<head>
  <meta charset="UTF-8">
  <title>Productieplanning — ${datumLang}</title>
  <style>
    *, *::before, *::after {
      box-sizing: border-box;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    body { font-family: Arial, Helvetica, sans-serif; background: #EAEAEA; margin: 0; padding: 16px; }
    @media print {
      body { background: #ffffff; padding: 0; }
      @page { size: A4 landscape; margin: 6mm 8mm; }
      input[type="checkbox"] { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    }
  </style>
</head>
<body>
${blokken}
</body>
</html>`;
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function PrintPlanningPage({ user }) {
  const [stappen, setStappen] = useState([]);
  const [laden, setLaden]     = useState(true);
  const [kiesAfd, setKiesAfd] = useState('alle');
  const nav = useNavigate();

  useEffect(() => {
    api.get('/orders/actieve-stappen').then(r => {
      setStappen(r.data);
      setLaden(false);
    });
  }, []);

  const datumLang = new Date().toLocaleDateString('nl-BE', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  const afdsMetStappen = afdelingen
    .map(afd => {
      const lijst = stappen
        .filter(s => afd.operatieNummers.includes(parseInt(s.operatie_nummer)))
        .sort((a, b) => {
          const prio = { spoed: 0, hoog: 1, normaal: 2 };
          const dp = (prio[a.prioriteit] ?? 2) - (prio[b.prioriteit] ?? 2);
          return dp !== 0 ? dp : parseInt(a.dagen_resterend) - parseInt(b.dagen_resterend);
        });
      return { afd, lijst };
    })
    .filter(({ afd, lijst }) => {
      if (kiesAfd !== 'alle' && afd.id !== kiesAfd) return false;
      return lijst.length > 0;
    });

  // ── Afdrukken via nieuw venster — 100% controle over output ──────────────────
  const drukAf = async () => {
    // Logo als base64 embedden zodat het werkt in een blank venster zonder origin
    let logoData = '';
    try {
      const resp = await fetch('/logo.png');
      const blob = await resp.blob();
      logoData = await new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch(e) { /* logo niet beschikbaar, gewoon zonder */ }

    const html = maakPrintHTML(afdsMetStappen, datumLang, logoData);
    const w = window.open('', '_blank');
    if (!w) { alert('Pop-upblokkering actief — sta pop-ups toe voor deze pagina.'); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.onload = () => { w.focus(); w.print(); };
    setTimeout(() => { try { w.focus(); w.print(); } catch(e) {} }, 800);
  };

  // ── Schermversie rijrenderer ──────────────────────────────────────────────────
  const SchermRij = ({ stap, i }) => {
    const urg   = urgentieTekst(stap.dagen_resterend, stap.prioriteit);
    const rijBg = urg.bg !== '#fff' ? urg.bg : i % 2 === 0 ? '#fff' : '#F8F9FC';
    const specs = [
      stap.diameter_mm     ? `⌀ ${parseFloat(stap.diameter_mm)} mm` : '',
      stap.tanden_per_stuk ? `${stap.tanden_per_stuk} tanden/stuk`   : '',
      stap.is_extern       ? `▶ Extern: ${stap.externe_partij || ''}` : '',
    ].filter(Boolean).join('  ·  ');

    return (
      <tr style={{ borderBottom: '1px solid #EEE', background: rijBg }}>
        {/* Kolom 1: urgentie + ordernummer + leverdatum gestapeld */}
        {/* Kolom 1: urgentie + datum */}
        <td style={{ padding: '5px 7px', verticalAlign: 'top', width: 72, whiteSpace: 'nowrap' }}>
          <div style={{ fontWeight: 800, fontSize: 9, color: urg.kleur, marginBottom: 2 }}>{urg.txt}</div>
          <div style={{ fontSize: 8, color: '#555' }}>📅 {datFmt(stap.leverdatum)}</div>
        </td>
        {/* Kolom 2: ordernummer + klant op 1 lijn */}
        <td style={{ padding: '5px 7px', verticalAlign: 'top' }}>
          <div style={{ fontSize: 10, marginBottom: 1 }}>
            <span style={{ fontWeight: 700, color: '#1F3864' }}>{stap.ordernummer}</span>
            <span style={{ color: '#BBB', margin: '0 4px' }}>·</span>
            <span style={{ fontWeight: 600, color: '#222' }}>{stap.klant}</span>
          </div>
          <div style={{ fontSize: 9, color: '#555', marginTop: 1 }}>
            <em>{stap.label || stap.operatie_naam}</em>
            {stap.artikel_omschrijving && ` — ${stap.artikel_omschrijving}`}
          </div>
          {specs && <div style={{ fontSize: 8, color: '#777', marginTop: 1 }}>{specs}</div>}
          {stap.klant_referentie && <div style={{ fontSize: 8, color: '#999', marginTop: 1 }}>ref: {stap.klant_referentie}</div>}
        </td>
        {/* Kolom 3: aantal */}
        <td style={{ padding: '5px 7px', textAlign: 'center', fontWeight: 700, fontSize: 11, color: '#222', verticalAlign: 'top', width: 32 }}>{stap.aantal}</td>
        {/* Kolom 4: instructies */}
        <td style={{ padding: '5px 7px', fontSize: 9, color: stap.instructies ? '#333' : '#CCC', fontStyle: stap.instructies ? 'normal' : 'italic', verticalAlign: 'top' }}>
          {stap.instructies || '—'}
        </td>
        {/* Kolom 5: checkbox */}
        <td style={{ padding: '5px 7px', textAlign: 'center', verticalAlign: 'top', width: 28 }}>
          <input type="checkbox" style={{ width: 13, height: 13, cursor: 'pointer', accentColor: '#1F3864' }} />
        </td>
      </tr>
    );
  };

  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', background: '#EAEAEA', minHeight: '100vh' }}>

      {/* ── Schermbalk ───────────────────────────────────────────────────────── */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: '#1F3864', color: '#fff',
        padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      }}>
        <img src="/logo.png" style={{ height: 30 }} alt="Spoo Linssen" />
        <span style={{ fontWeight: 700, fontSize: 15 }}>Print planning</span>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, opacity: 0.7 }}>{datumLang}</span>

        <select value={kiesAfd} onChange={e => setKiesAfd(e.target.value)}
          style={{ padding: '7px 12px', borderRadius: 6, border: 'none', fontSize: 13, cursor: 'pointer', minWidth: 160 }}>
          <option value="alle">Alle afdelingen</option>
          {afdelingen.map(a => <option key={a.id} value={a.id}>{a.naam}</option>)}
        </select>

        <button onClick={drukAf}
          style={{ padding: '9px 22px', background: '#E8A020', color: '#fff', border: 'none', borderRadius: 7, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
          🖨️ Afdrukken
        </button>
        <button onClick={() => nav('/productie')}
          style={{ padding: '9px 16px', background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 7, fontSize: 13, cursor: 'pointer' }}>
          ← Terug
        </button>
      </div>

      {/* ── Schermpreview ────────────────────────────────────────────────────── */}
      {laden ? (
        <div style={{ padding: 60, textAlign: 'center', color: '#888', fontSize: 14 }}>Laden...</div>
      ) : afdsMetStappen.length === 0 ? (
        <div style={{ padding: 60, textAlign: 'center', color: '#888', fontSize: 14 }}>Geen actieve taken gevonden.</div>
      ) : (
        <div style={{ padding: '16px', maxWidth: 1150, margin: '0 auto' }}>
          {afdsMetStappen.map(({ afd, lijst }) => {
            const matItems = lijst.filter(s => s.materiaal_beschrijving || s.materiaal_referentie);
            return (
              <div key={afd.id} style={{ background: '#fff', borderRadius: 8, boxShadow: '0 2px 12px rgba(0,0,0,0.12)', marginBottom: 28, overflow: 'hidden' }}>

                {/* Header */}
                <div style={{ background: '#1F3864', padding: '9px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                  {/* Logo links in wit kader */}
                  <div style={{ flexShrink: 0, background: '#ffffff', borderRadius: 4, padding: '3px 8px', display: 'flex', alignItems: 'center' }}>
                    <img src="/logo.png" alt="Spoo Linssen" style={{ height: 26, display: 'block' }} />
                  </div>
                  {/* Afdelingsnaam gecentreerd */}
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 2 }}>Productieplanning</div>
                    <div style={{ fontSize: 17, fontWeight: 800, color: '#fff', letterSpacing: 0.3 }}>{afd.naam}</div>
                  </div>
                  {/* Datum en aantal rechts */}
                  <div style={{ flexShrink: 0, textAlign: 'right' }}>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.55)', marginBottom: 1 }}>Afgedrukt op</div>
                    <div style={{ fontSize: 11, color: '#fff', fontWeight: 500 }}>{datumLang}</div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginTop: 3 }}>{lijst.length} taak{lijst.length !== 1 ? 'en' : ''}</div>
                  </div>
                </div>

                {/* Tabel */}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                  <thead>
                    <tr style={{ background: '#F0F4FA', borderBottom: '1px solid #D0D8E8' }}>
                      <th style={{ padding: '4px 7px', textAlign: 'left', fontWeight: 700, fontSize: 9, color: '#1F3864', width: 72 }}>Urgentie / Datum</th>
                      <th style={{ padding: '4px 7px', textAlign: 'left', fontWeight: 700, fontSize: 9, color: '#1F3864' }}>Order / Klant / Artikel</th>
                      <th style={{ padding: '4px 7px', textAlign: 'center', fontWeight: 700, fontSize: 9, color: '#1F3864', width: 32 }}>Aant.</th>
                      <th style={{ padding: '4px 7px', textAlign: 'left', fontWeight: 700, fontSize: 9, color: '#1F3864' }}>Instructies</th>
                      <th style={{ padding: '4px 7px', textAlign: 'center', fontWeight: 700, fontSize: 9, color: '#1F3864', width: 28 }}>✓</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lijst.map((stap, i) => <SchermRij key={stap.id} stap={stap} i={i} />)}
                  </tbody>
                </table>

                {/* Materiaal */}
                {matItems.length > 0 && (
                  <div style={{ padding: '10px 22px', background: '#FFF8F0', borderTop: '1px solid #F0D0A0' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#8A5C00' }}>📦 Materiaal:&nbsp;</span>
                    {matItems.map(s => (
                      <span key={s.id} style={{ fontSize: 11, color: '#5C3D00', marginRight: 16 }}>
                        <strong>{s.ordernummer}</strong>: {s.materiaal_beschrijving || ''}{s.materiaal_referentie ? ` (${s.materiaal_referentie})` : ''}
                        {s.materiaal_status !== 'in_stock' && <span style={{ color: '#C0392B', fontWeight: 600, marginLeft: 4 }}>[{s.materiaal_status === 'te_bestellen' ? 'te bestellen' : s.materiaal_status}]</span>}
                      </span>
                    ))}
                  </div>
                )}


              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
